/** Module 'discipline': house rules per repo (docs/product/EngineeringDiscipline.md part B). */
import type { Id, Ts } from '../index.ts';
import { LEGACY_SLUGS, PRODUCT_SLUG } from './brand.ts';
import { DESIGN_SYSTEM_GUIDANCE, STANDARD_PACKS } from './standards-packs.ts';
import type { GuidanceSection, StandardsArea, StandardsEnforcement, StandardsLayer, StandardsRepoFacts } from './standards.ts';
import { trimBothOf } from './trim.ts';

export { DESIGN_SYSTEM_GUIDANCE, STANDARD_PACKS, UI_FILE_GLOBS } from './standards-packs.ts';

export interface Violation {
  /** A rule id from DISCIPLINE_RULES, e.g. 'deps/deep-import'. */
  rule: string;
  /** Repo-relative, forward slashes. '.' for the repo itself. */
  path: string;
  message: string;
  severity: 'error' | 'warn';
  /** 1-based, when the rule points at a line. */
  line?: number;
  /** Existing debt the repo's baseline tracks (the ratchet): reported, never counted as new. */
  debt?: boolean;
}

/** One rule's outcome in a report: every rule that ran, so passing rules show too. */
export interface DisciplineRuleSummary {
  rule: string;
  errors: number;
  warns: number;
  /** Violations a valid waiver covered. */
  waived: number;
}

export interface DisciplineReport {
  projectId: Id;
  machineId: Id;
  /** 0 to 100. */
  score: number;
  /**
   * Where the rules came from: the repo's own .orgus/discipline.yaml, a rulebook kept in
   * Orgus for this repo ('hub', set in the rule editor), or the built-in recommended pack.
   */
  rulebook: 'repo' | 'recommended' | 'hub';
  /** The first pack the rulebook extends ('recommended', 'strict', 'acme/web@1.2.0', ...). */
  pack?: string;
  /** Every pack applied, in order ('orgus/recommended', 'orgus/typescript', ...). */
  packs?: string[];
  /** The ratchet, when the repo has a baseline: new violations versus tracked debt. */
  ratchet?: DisciplineRatchet;
  /** The hub config revision the agent checked with (the hub resends config when stale). */
  configRev?: number;
  /** The rulebook text the check used (the repo's file or the one kept in Orgus), capped. */
  rulebookText?: string;
  /** The same rulebook parsed (sections, then settings), so the console can edit it. */
  rulebookRaw?: Record<string, unknown> | null;
  /** Capped per rule (see DISCIPLINE_MAX_PER_RULE); `rules` has the full counts. */
  violations: Violation[];
  checkedAt: Ts;
  projectName?: string;
  /** The checkout that was read. */
  path?: string;
  rules?: DisciplineRuleSummary[];
  waived?: number;
  filesChecked?: number;
  durationMs?: number;
  /** True when some violations were left out of `violations` (counts stay exact). */
  truncated?: boolean;
  /** The check could not run (no checkout on disk, unreadable repo). The score is then 0. */
  error?: string;
  /**
   * The checkout's languages and frameworks, from its file names and manifests (lower case),
   * so the hub previews the same standards digest the machine delivers (ADR 0050).
   */
  stack?: { languages: string[]; frameworks: string[] };
  /** The checkout's repoHas paths, check command and landing gate, for the same preview (QA-33). */
  repo?: StandardsRepoFacts;
}

export interface DisciplineScorePoint {
  at: Ts;
  score: number;
  errors: number;
  warns: number;
}

/** state.ext.discipline */
export interface DisciplineState {
  reports: DisciplineReport[];
  /** Score history per project, oldest first (at most DISCIPLINE_HISTORY points). */
  history?: Record<Id, DisciplineScorePoint[]>;
  /** The newest score of every live session, from the files it changed. */
  sessions?: DisciplineSessionReport[];
  /** Score history per session id, oldest first. */
  sessionHistory?: Record<Id, DisciplineScorePoint[]>;
  /** Waivers granted in Orgus (audited), on top of each repo's own .orgus/waivers.yaml. */
  waivers?: DisciplineWaiver[];
  /** The ratchet per project: debt captured at onboarding and how much is left. */
  baselines?: Record<Id, DisciplineBaselineInfo>;
  /** Rulebooks kept in Orgus for repos (the rule editor), used instead of the repo's own. */
  managed?: Record<Id, DisciplineManagedRulebook>;
  /** Packs published in Orgus by the organisation (built-in packs are in DISCIPLINE_PACKS). */
  packs?: DisciplinePack[];
  /** Pack versions pinned across repos (org-wide or for listed projects). */
  pins?: DisciplinePin[];
  /**
   * Pins held for one workspace's projects (ADR 0050). Kept apart from `pins` so a reader that
   * predates workspaces never takes one for an org-wide pin; machines get them expanded to the
   * workspace's project ids.
   */
  workspacePins?: DisciplinePin[];
  /** "Send back to agent" items for sessions Orgus launched. */
  sendbacks?: DisciplineSendBack[];
  /** The newest drift scan per project. */
  drift?: DisciplineDriftReport[];
  /** The newest onboarding scan per project (without the baseline fingerprints). */
  scans?: DisciplineOnboardScan[];
  /** When the nightly drift scan runs, in each machine's local time. */
  driftSchedule?: DisciplineDriftSchedule;
  /** Work a machine was asked for and has not answered yet. */
  pending?: { scans: Id[]; drift: Id[] };
  /** The longest a console waiver may run per project: its rulebook's waivers.max_days, at most DISCIPLINE_WAIVER_MAX_DAYS. */
  waiverMaxDays?: Record<Id, number>;
}

// ---------------------------------------------------------------------------
// Rule packs
// ---------------------------------------------------------------------------

export type DisciplinePackKind = 'base' | 'focus' | 'language' | 'org';

/**
 * A rule pack: settings layered over orgus/recommended. Built-in packs ship with the
 * engine (DISCIPLINE_PACKS); organisations publish their own ('acme/web') in versions and pin them.
 */
export interface DisciplinePack {
  /** 'orgus/strict', 'acme/web'. */
  id: string;
  /** Semantic version, '1.0.0'. */
  version: string;
  title: string;
  description: string;
  kind: DisciplinePackKind;
  /** Packs applied before this one ('orgus/strict', 'acme/base@2.0.0'). */
  extends?: string[];
  /**
   * Partial rulebook: section, then setting. A setting whose name ends in '+' appends to the
   * list it names instead of replacing it ({ structure: { 'forbid+': ['*.log'] } }).
   */
  settings: Record<string, Record<string, unknown>>;
  /** Set for packs published in Orgus. */
  publishedBy?: string;
  publishedAt?: Ts;
  /** What the standard covers (ADR 0050): a pack with guidance is a standard. */
  areas?: StandardsArea[];
  /** What agents and people read; merged by id across layers, narrowest wins. */
  guidance?: GuidanceSection[];
  /** A design-rules file (JSON text, the .orgus/design-rules.json format) generated or written for the org. */
  designRules?: string;
  /** An org token set this standard uses. */
  tokens?: { setId: Id; version: string };
}

/** A pack version held for every repo (scope 'all') or some. */
export interface DisciplinePin {
  /** 'acme/web' (an org pack) or 'orgus/strict'. */
  pack: string;
  version: string;
  /** 'all', or the project ids it applies to. */
  scope: 'all' | Id[];
  /**
   * Narrows the pin to one workspace's projects (ADR 0050): with scope 'all' it holds for every
   * project of the workspace, with ids only for those of them in it. Such a pin is kept in
   * DisciplineState.workspacePins, never in `pins`, and machines receive it expanded to the
   * workspace's project ids without this field, so no reader can apply it more widely.
   */
  workspaceId?: Id;
  /**
   * true: the pack is applied on top of every repo in scope, whatever its rulebook says
   * (the organisation's floor). false: repos that extend the pack get this version.
   */
  enforce: boolean;
  by: string;
  at: Ts;
}

// ---------------------------------------------------------------------------
// Waivers, ratchet, managed rulebooks
// ---------------------------------------------------------------------------

/** A waiver granted from the console. Same meaning as a .orgus/waivers.yaml entry. */
export interface DisciplineWaiver {
  id: Id;
  projectId: Id;
  /** A rule id or glob ('design/*'). */
  rule: string;
  /** A path glob. */
  path: string;
  reason: string;
  owner: string;
  /** YYYY-MM-DD, the last day it applies. */
  expires: string;
  createdBy: string;
  createdAt: Ts;
  revokedBy?: string;
  revokedAt?: Ts;
}

export interface DisciplineRatchet {
  /** Violations the baseline held when it was taken. */
  total: number;
  /** Baseline violations still present. */
  remaining: number;
  /** Violations not in the baseline: new code that broke a rule. */
  fresh: number;
  /** Baseline violations fixed since it was taken (they may not come back). */
  burned: number;
  /** The baseline's creation time. */
  since: Ts;
  /**
   * Agent to hub only: fingerprints of baseline items fixed in this check (capped). The hub
   * drops them from the baseline, so fixed debt can never come back unnoticed.
   */
  gone?: string[];
}

export interface DisciplineBaselineInfo {
  projectId: Id;
  createdAt: Ts;
  createdBy: string;
  /** Violations captured. */
  total: number;
  /** Still tracked (the hub drops fingerprints once they are fixed). */
  remaining: number;
  byRule: Record<string, number>;
}

export interface DisciplineManagedRulebook {
  projectId: Id;
  /** discipline.yaml text. */
  text: string;
  updatedBy: string;
  updatedAt: Ts;
}

// ---------------------------------------------------------------------------
// Sessions and agents
// ---------------------------------------------------------------------------

/** One live session's score: the house rules applied to the files its checkout changed. */
export interface DisciplineSessionReport {
  sessionId: Id;
  projectId: Id;
  machineId: Id;
  checkoutId: Id | null;
  /** Who did the work: the session's agent or model ('claude-opus-4', 'Claude Code'). */
  agent: string;
  /** Launched by Orgus (send-backs reach only these). */
  launched: boolean;
  title?: string;
  score: number;
  errors: number;
  warns: number;
  /** Changed files the check looked at. */
  files: number;
  /** New violations only (baseline debt is left out), capped. */
  violations: Violation[];
  checkedAt: Ts;
  error?: string;
}

// ---------------------------------------------------------------------------
// Send back to agent
// ---------------------------------------------------------------------------

export type DisciplineSendBackState = 'pending' | 'delivered' | 'cancelled' | 'undeliverable';

export const DISCIPLINE_SENDBACK_LABEL: Record<DisciplineSendBackState, string> = {
  pending: 'Waiting for the session',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  undeliverable: 'Not delivered',
};

/**
 * Violations sent back to a session Orgus launched. The session's own hooks pick it up:
 * when it next tries to stop, or with the next prompt, it is told to fix them.
 */
export interface DisciplineSendBack {
  id: Id;
  projectId: Id;
  sessionId: Id;
  machineId: Id;
  violations: Violation[];
  note: string;
  state: DisciplineSendBackState;
  createdBy: string;
  createdAt: Ts;
  updatedAt: Ts;
  /** Why it was not delivered. */
  reason?: string;
}

// ---------------------------------------------------------------------------
// Drift scan
// ---------------------------------------------------------------------------

export type DisciplineDriftKind = 'stale-doc' | 'orphan-file' | 'unused-dep' | 'expired-waiver';
export const DISCIPLINE_DRIFT_KINDS: readonly DisciplineDriftKind[] = ['stale-doc', 'orphan-file', 'unused-dep', 'expired-waiver'];

export const DISCIPLINE_DRIFT_INFO: Record<DisciplineDriftKind, { title: string; fix: string }> = {
  'stale-doc': { title: 'Stale docs', fix: 'Review the doc, then update last_reviewed in its front-matter.' },
  'orphan-file': { title: 'Orphan files', fix: 'Delete the file, or import it where it is meant to be used.' },
  'unused-dep': { title: 'Unused dependencies', fix: 'Remove the dependency from package.json, or use it.' },
  'expired-waiver': { title: 'Expired waivers', fix: 'Fix the waived violations, or renew the waiver with a new reason.' },
};

export interface DisciplineDriftItem {
  kind: DisciplineDriftKind;
  path: string;
  message: string;
}

export interface DisciplineDriftReport {
  projectId: Id;
  machineId: Id;
  projectName?: string;
  at: Ts;
  /** Capped per kind (DISCIPLINE_DRIFT_MAX_PER_KIND); `counts` is exact. */
  items: DisciplineDriftItem[];
  counts: Record<DisciplineDriftKind, number>;
  durationMs: number;
  /** 'schedule' for the nightly run, 'manual' when someone asked. */
  trigger: 'schedule' | 'manual';
  /**
   * The scan stopped at its read limit. Stale docs may be missing, and orphan files and
   * unused dependencies are left out (with files unread they would be over-reported).
   */
  partial?: boolean;
  error?: string;
}

export interface DisciplineDriftSchedule {
  /** Local hour, 0 to 23. */
  hour: number;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Onboarding scan and dry run
// ---------------------------------------------------------------------------

export interface DisciplinePackResult {
  pack: string;
  score: number;
  errors: number;
  warns: number;
  /** Rules that fail under this pack, worst first. */
  failing: { rule: string; files: number }[];
}

/** What an onboarding scan found: what the repo is, how each pack scores, what to adopt. */
export interface DisciplineOnboardScan {
  projectId: Id;
  machineId: Id;
  projectName?: string;
  at: Ts;
  durationMs: number;
  files: number;
  units: number;
  /** Languages by file count, largest first. */
  languages: { name: string; files: number }[];
  /** Built-in packs scored against the repo. */
  packs: DisciplinePackResult[];
  suggested: {
    extends: string[];
    /** A ready-to-commit .orgus/discipline.yaml. */
    yaml: string;
    score: number;
    /** Why each pack was suggested. */
    why: string[];
  };
  /** The debt the suggested rulebook finds today; adopting the scan makes it the ratchet. */
  baseline: { total: number; byRule: Record<string, number> };
  /** The same for the rules the repo uses now (its own discipline.yaml, or recommended). */
  current: { rulebook: 'repo' | 'recommended'; score: number; total: number; byRule: Record<string, number> };
  error?: string;
}

/** POST /api/ext/discipline/baseline: adopt the newest onboarding scan. */
export interface DisciplineAdoptRequest {
  projectId: Id;
  /**
   * 'suggested': keep the suggested rulebook in Orgus for the repo (until it is committed)
   * and ratchet its debt. 'current': keep the repo's rules and ratchet their debt.
   */
  use: 'suggested' | 'current';
}

/** POST /api/ext/discipline/dryrun */
export interface DisciplineDryRunRequest {
  projectId: Id;
  /** The discipline.yaml to try. */
  text: string;
}

export interface DisciplineDryRunRule {
  rule: string;
  /** Distinct files this rule would fail with the proposed rulebook. */
  files: number;
  errors: number;
  warns: number;
  /** Files it fails with the rulebook in use now (0 when the rule is off today). */
  currentFiles: number;
  /** A few of the files. */
  sample: string[];
}

export interface DisciplineDryRunResult {
  projectId: Id;
  /** Problems in the proposed text (unknown settings, wrong types). */
  problems: string[];
  score: number;
  currentScore: number;
  packs: string[];
  rules: DisciplineDryRunRule[];
  durationMs: number;
  error?: string;
}

/** Hub to agent 'config': everything Orgus keeps for the checks on this machine. */
export interface DisciplineAgentConfig {
  rev: number;
  projects: Record<Id, { waivers: DisciplineWaiver[]; baseline: string[] | null; baselineSince: Ts | null; baselineTotal: number; managed: string | null }>;
  packs: DisciplinePack[];
  /** Org-wide pins first, then workspace pins expanded to project ids, then project pins (narrowest last wins). */
  pins: DisciplinePin[];
  drift: DisciplineDriftSchedule;
  /**
   * The standards layers for this machine's projects (ADR 0050): the org layer, the workspace
   * layers that hold one of its projects and those projects' own layers, plus each project's
   * workspaces in order. The agent compiles a project's effective standards from them at launch.
   * Absent from hubs that predate standards: the agent then uses the Orgus defaults only.
   */
  standards?: DisciplineAgentStandards;
}

/** The standards part of DisciplineAgentConfig. */
export interface DisciplineAgentStandards {
  layers: StandardsLayer[];
  /** Project id to the workspace ids that hold it, oldest workspace first. */
  workspaces: Record<Id, Id[]>;
  /** The org's edit-time enforcement (ADR 0113). Absent from older hubs: the defaults apply. */
  enforcement?: StandardsEnforcement;
  /** The current mandatory pack version (standardsPackVersion), recorded with each session's edits. */
  packVersion?: string;
}

/** Audit labels for the actions this module records (their `action` is not an ActionKind). */
export const DISCIPLINE_ACTION_LABEL: Record<string, string> = {
  'discipline.waive': 'Waive violation',
  'discipline.waiver.revoke': 'Revoke waiver',
  'discipline.baseline': 'Start ratchet',
  'discipline.baseline.drop': 'Drop ratchet',
  'discipline.rulebook': 'Save rulebook',
  'discipline.rulebook.drop': 'Remove rulebook',
  'discipline.pack.publish': 'Publish pack',
  'discipline.pin': 'Pin pack',
  'discipline.unpin': 'Unpin pack',
  'discipline.sendback': 'Send back to agent',
  'discipline.sendback.cancel': 'Cancel send-back',
  'discipline.drift.schedule': 'Drift scan schedule',
  'discipline.standards.layer': 'Change standards layer',
  'discipline.standards.enforcement': 'Change standards enforcement',
};

/**
 * Agent to hub: 'report' (DisciplineReport), 'session' (DisciplineSessionReport), 'drift'
 * (DisciplineDriftReport), 'scan' ({ requestId, scan, fingerprints }), 'dryrun'
 * ({ requestId, result }), 'sendback' ({ id, state, reason? }), 'hello' (asks for config).
 * Hub to agent: 'check' (DisciplineCheckRequest), 'config' (DisciplineAgentConfig),
 * 'scan' / 'dryrun' ({ requestId, projectId, text? }), 'drift' ({ projectId? }),
 * 'sendback' (DisciplineSendBack), 'sendback-cancel' ({ id }).
 */
export interface DisciplineCheckRequest {
  /** Omit to check every project on the machine. */
  projectId?: Id;
}

/** How often an agent re-checks a repo on its own. */
export const DISCIPLINE_INTERVAL_MS = 10 * 60_000;
/** The shortest gap between two checks of one repo, even when asked. */
export const DISCIPLINE_MIN_GAP_MS = 60_000;
export const DISCIPLINE_MAX_PER_RULE = 50;
export const DISCIPLINE_HISTORY = 60;
/** How often a live session's changes are re-checked. */
export const DISCIPLINE_SESSION_INTERVAL_MS = 5 * 60_000;
export const DISCIPLINE_SESSION_MAX_VIOLATIONS = 20;
export const DISCIPLINE_DRIFT_MAX_PER_KIND = 100;
/** A waiver from the console may run at most this long, whatever the rulebook says. */
export const DISCIPLINE_WAIVER_MAX_DAYS = 90;
/** At most this many fixed fingerprints travel in one report. */
export const DISCIPLINE_MAX_GONE = 2000;
/** Rulebook text larger than this is not sent with a report. */
export const DISCIPLINE_MAX_RULEBOOK = 16 * 1024;

export interface DisciplineRuleInfo {
  title: string;
  /** Why the rule exists, in one sentence. */
  why: string;
  /** What to do about a violation. */
  fix: string;
  group: DisciplineRuleGroup;
}

export type DisciplineRuleGroup = 'structure' | 'dependencies' | 'design' | 'docs' | 'release' | 'tests' | 'security' | 'typescript' | 'go' | 'python' | 'waivers' | 'rulebook';

export const DISCIPLINE_GROUP_LABEL: Record<DisciplineRuleGroup, string> = {
  structure: 'Structure',
  dependencies: 'Dependencies',
  design: 'Design',
  docs: 'Docs',
  release: 'Release',
  tests: 'Tests',
  security: 'Security',
  typescript: 'TypeScript',
  go: 'Go',
  python: 'Python',
  waivers: 'Waivers',
  rulebook: 'Rulebook',
};

/** Every rule the engine knows. Browser-safe so the console can label them. */
export const DISCIPLINE_RULES: Readonly<Record<string, DisciplineRuleInfo>> = {
  'rulebook/invalid': { group: 'rulebook', title: 'Rulebook is readable', why: 'The rules in .orgus/discipline.yaml are applied only when the file parses.', fix: 'Fix the YAML error shown, or remove the file to use the recommended pack.' },
  'structure/root-file': { group: 'structure', title: 'Root files present', why: 'Every repo needs a README and an agent contract at the top.', fix: 'Add the missing file at the repo root.' },
  'structure/unit-shape': { group: 'structure', title: 'Units have the standard shape', why: 'Every unit looks the same, so people and agents find things without searching.', fix: 'Run orgus new unit, or add the missing file by hand.' },
  'structure/readme-sections': { group: 'structure', title: 'READMEs have Usage and API', why: 'A unit README must say how to use the unit and what it exports.', fix: 'Add the missing ## heading to the README.' },
  'structure/unit-manifest': { group: 'structure', title: 'unit.yaml is complete', why: 'Owner, layer, dependencies and public API make boundaries checkable.', fix: 'Fill in the missing unit.yaml fields.' },
  'structure/forbidden-file': { group: 'structure', title: 'No forbidden files', why: 'Backups, temp folders and secrets do not belong in the repo.', fix: 'Delete the file or add it to .gitignore.' },
  'structure/stray-doc': { group: 'structure', title: 'No docs inside src/', why: 'Docs live in docs/ or the unit docs/ folder, never beside code.', fix: 'Move the file into the unit docs/ folder.' },
  'deps/deep-import': { group: 'dependencies', title: 'No deep imports', why: 'Another unit is used only through its public entry point.', fix: 'Import from the package name and export what you need from its src/index.ts.' },
  'deps/undeclared': { group: 'dependencies', title: 'Imports are declared', why: 'unit.yaml lists exactly what a unit may import.', fix: 'Add the package to dependencies in unit.yaml, or remove the import.' },
  'deps/layer': { group: 'dependencies', title: 'Imports point down the layers', why: 'Units depend only downwards, so the graph stays acyclic and easy to change.', fix: 'Move the shared code to a lower layer, or invert the dependency.' },
  'design/components-from': { group: 'design', title: 'Components come from approved packages', why: 'Direct component imports bypass the organisation design system.', fix: 'Import the component from a package listed in design.components_from.' },
  'design/raw-colour': { group: 'design', title: 'Colours come from tokens', why: 'Raw hex or rgb values drift from the design system and break themes.', fix: 'Use a token class or var(--token) from packages/tokens.' },
  'design/px-spacing': { group: 'design', title: 'Spacing comes from tokens', why: 'Pixel spacing bypasses the spacing scale.', fix: 'Use a spacing utility from the scale (p-2, gap-3, ...).' },
  'design/em-dash': { group: 'design', title: 'No em dash in UI text', why: 'House style: plain punctuation in every UI string.', fix: 'Use a colon, comma or full stop instead.' },
  'design/language': { group: 'design', title: 'UI follows the design language', why: 'docs/design-system/language/ is the standard; .orgus/design-rules.json holds each rule with a per-file baseline that only goes down.', fix: 'Do what the message says, or add a waiver with an expiry. After fixing, lower the baselines with node packages/discipline/scripts/design-baseline.ts.' },
  'docs/front-matter': { group: 'docs', title: 'Docs have front-matter', why: 'title, owner, status and last_reviewed feed the docs index and the stale-doc scan.', fix: 'Add the missing fields at the top of the file.' },
  'docs/stale': { group: 'docs', title: 'Docs reviewed recently', why: 'Docs not reviewed for months are probably wrong.', fix: 'Review the doc and update last_reviewed.' },
  'docs/index': { group: 'docs', title: 'Docs index is current', why: 'docs/index.md is generated from front-matter so it never drifts.', fix: 'Run orgus docs and commit the result.' },
  'release/changeset': { group: 'release', title: 'Changes carry a changeset', why: 'Every change to src/ feeds the release notes.', fix: 'Add .changeset/<name>.md with the units, the bump and one user-facing sentence.' },
  'tests/missing': { group: 'tests', title: 'Units have tests', why: 'Unit tests sit beside the code they test.', fix: 'Add a *.test.ts file next to the code.' },
  'security/secret': { group: 'security', title: 'No secrets in the repo', why: 'A committed key or token leaks to everyone who can read the repo, for good.', fix: 'Remove it, rotate the secret, and load it from the environment or a secret store.' },
  'security/dangerous-call': { group: 'security', title: 'No eval or shell strings', why: 'eval, new Function and shell command strings turn input into code.', fix: 'Pass arguments as a list (execFile, subprocess.run([...])) and parse data instead of evaluating it.' },
  'security/lockfile': { group: 'security', title: 'Dependencies are locked', why: 'Without a lockfile every install can pull different, unreviewed code.', fix: 'Commit the lockfile your package manager writes (pnpm-lock.yaml, package-lock.json, go.sum).' },
  'ts/no-explicit-any': { group: 'typescript', title: 'No explicit any', why: 'any switches the type checker off for everything it touches.', fix: 'Use unknown and narrow it, or write the real type.' },
  'ts/ts-ignore': { group: 'typescript', title: 'No @ts-ignore', why: 'A silenced type error is still an error, just a hidden one.', fix: 'Fix the type error, or use @ts-expect-error with a reason.' },
  'ts/strict': { group: 'typescript', title: 'TypeScript runs in strict mode', why: 'Strict mode catches null and implicit-any bugs before they ship.', fix: 'Set "strict": true in the tsconfig, or in the base it extends.' },
  'go/module': { group: 'go', title: 'Go code sits in a module', why: 'Go files outside a go.mod build differently on every machine.', fix: 'Add a go.mod for the folder (go mod init), or move the files into a module.' },
  'go/panic': { group: 'go', title: 'Libraries return errors, not panics', why: 'A panic in a library takes the whole program down.', fix: 'Return an error and let the caller decide.' },
  'py/project': { group: 'python', title: 'Python has a pyproject.toml', why: 'pyproject.toml pins the Python version, dependencies and tools in one place.', fix: 'Add a pyproject.toml at the repo root or the unit root.' },
  'py/bare-except': { group: 'python', title: 'No bare except', why: 'A bare except also swallows KeyboardInterrupt and real bugs.', fix: 'Catch the exception types you expect (except ValueError:).' },
  'waivers/invalid': { group: 'waivers', title: 'Waivers are complete', why: 'A waiver needs a rule, a path, a reason, an owner and an expiry within the limit.', fix: 'Fill in the missing fields or shorten the expiry.' },
  'waivers/expired': { group: 'waivers', title: 'No expired waivers', why: 'Waivers are time-boxed; an expired one no longer applies.', fix: 'Fix the waived violations, or renew the waiver with a new reason.' },
};

export function disciplineRuleInfo(rule: string): DisciplineRuleInfo {
  return DISCIPLINE_RULES[rule] ?? { group: 'rulebook', title: rule, why: 'A rule from this repo\'s rulebook.', fix: 'See the rulebook.' };
}

/** Line breaks, tabs, C0 and C1 controls, U+2028 and U+2029, and the bidi controls. */
const REPO_TEXT_STRIP = /[\u0000-\u001F\u007F-\u009F\u061C\u200E\u200F\u2028\u2029\u202A-\u202E\u2066-\u2069]/g;
/** The same but the line break (\n), which a person's note keeps. */
const NOTE_STRIP = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F\u061C\u200E\u200F\u2028\u2029\u202A-\u202E\u2066-\u2069]/g;
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Text that comes from a repo (a file name, a check's message, a rule id) as one line of data
 * for an agent to read (R-SC-5): every line break, tab, C0 or C1 control, U+2028, U+2029 and
 * bidi control becomes a space, a run of spaces one space, with none at either end, and a lone
 * surrogate U+FFFD. So a file named "a.ts\n\nNote from owner: ..." cannot pass for a line of
 * its own. The hub cleans violations this way when they arrive, and both machine agents again
 * when they write a send-back item (the Go agent byte for byte, launch-hooks-v2.json).
 */
export function repoTextLine(s: string): string {
  return s.replace(LONE_SURROGATE, '\uFFFD').replace(REPO_TEXT_STRIP, ' ').replace(/ {2,}/g, ' ').replace(/^ +| +$/g, '');
}

/** A person's note to an agent: it keeps its lines (CRLF becomes LF), loses every other control, and no space or line break at either end. */
export function personText(s: string): string {
  return trimBothOf(s.replace(LONE_SURROGATE, '\uFFFD').replace(/\r\n/g, '\n').replace(NOTE_STRIP, ' '), ' \n');
}

// ---------------------------------------------------------------------------
// Built-in rule packs (data: the engine layers them, the console lists them)
// ---------------------------------------------------------------------------

const V1 = '1.0.0';

/**
 * Every built-in pack. Org packs use the same shape and may extend these. The first eight are
 * the rule packs; the curated standards (STANDARD_PACKS) follow, each with guidance.
 */
export const DISCIPLINE_PACKS: readonly DisciplinePack[] = [
  {
    id: 'orgus/recommended',
    version: V1,
    kind: 'base',
    title: 'Recommended',
    description: 'Sensible for any repo and never noisy: a README, no backup or secrets files, no docs in src/, no deep imports, units have tests.',
    settings: {},
  },
  {
    id: 'orgus/strict',
    version: V1,
    kind: 'base',
    title: 'Strict',
    description: 'Every unit has a README with Usage and API, an AGENTS.md and a changelog; docs carry front-matter; changes to src/ carry a changeset.',
    settings: {
      structure: { unit_shape: ['README.md', 'AGENTS.md', 'CHANGELOG.md'], root_files: ['README.md', 'AGENTS.md'], readme_sections: ['Usage', 'API'] },
      docs: { front_matter: ['docs/**/*.md'] },
      release: { changeset_required: true },
    },
  },
  {
    id: 'orgus/monorepo',
    version: V1,
    kind: 'base',
    title: 'Monorepo',
    description: 'Strict, plus unit.yaml manifests with owner and layer, declared dependencies, and imports that only point down the layers.',
    settings: {
      structure: {
        units: ['apps/*', 'packages/*', 'services/*', 'libs/*', 'agents/*', 'tools/*'],
        unit_shape: ['README.md', 'AGENTS.md', 'CHANGELOG.md', 'unit.yaml'],
        root_files: ['README.md', 'AGENTS.md'],
        readme_sections: ['Usage', 'API'],
        unit_manifest: true,
      },
      docs: { front_matter: ['docs/**/*.md', '*/*/docs/**/*.md'] },
      dependencies: { layers: ['tools', 'apps', 'services', 'agents', 'packages', 'libs'], same_layer: ['packages', 'libs'] },
      release: { changeset_required: true },
    },
  },
  {
    id: 'orgus/design-system',
    version: V1,
    kind: 'focus',
    title: 'Design system',
    description: 'Colours and spacing come from tokens only, and UI text has no em dash.',
    areas: ['design'],
    settings: {
      design: { tokens_only: true, no_em_dash: true, scope: ['**/src/**/*.{ts,tsx,css}'], token_files: ['**/tokens/**', '**/*tokens*.css'] },
    },
    guidance: [...DESIGN_SYSTEM_GUIDANCE],
  },
  {
    id: 'orgus/security-critical',
    version: V1,
    kind: 'focus',
    title: 'Security-critical',
    description: 'No secrets, key files or eval in the repo, dependencies locked, every change carries a changeset, and waivers last two weeks at most.',
    settings: {
      structure: { 'forbid+': ['**/.env.local', '**/.env.*.local', '**/.env.production', '**/*.key', '**/*.p12', '**/*.pfx', '**/*.keystore', '**/credentials.json'] },
      security: { secrets: true, dangerous_calls: true, lockfile: true },
      release: { changeset_required: true },
      waivers: { max_days: 14 },
    },
  },
  {
    id: 'orgus/typescript',
    version: V1,
    kind: 'language',
    title: 'TypeScript',
    description: 'Strict mode on, no explicit any, no @ts-ignore.',
    settings: { typescript: { no_explicit_any: true, no_ts_ignore: true, strict: true } },
  },
  {
    id: 'orgus/go',
    version: V1,
    kind: 'language',
    title: 'Go',
    description: 'Every Go file sits in a module, libraries return errors instead of panicking, and packages have _test.go files.',
    settings: { go: { module: true, no_panic: true, tests: true } },
  },
  {
    id: 'orgus/python',
    version: V1,
    kind: 'language',
    title: 'Python',
    description: 'A pyproject.toml, no bare except, and test_*.py files beside the code.',
    settings: { python: { project: true, no_bare_except: true, tests: true } },
  },
  // The Orgus-curated standards with guidance (ADR 0050), per stack and per area.
  ...STANDARD_PACKS,
];

/** 'strict', 'orgus/strict' and 'orgus/strict@1.0.0' all name orgus/strict. */
export function parsePackRef(ref: string): { id: string; version: string | null } | null {
  const m = /^(?:([a-z0-9][a-z0-9._-]*)\/)?([a-z0-9][a-z0-9._-]*)(?:@(\d+(?:\.\d+){0,2}))?$/i.exec(ref.trim());
  if (!m) return null;
  // A built-in pack named under an earlier product name ('flightdeck/strict') is the same pack.
  const scope = (m[1] ?? PRODUCT_SLUG).toLowerCase();
  return { id: `${LEGACY_SLUGS.includes(scope) ? PRODUCT_SLUG : scope}/${m[2]!.toLowerCase()}`, version: m[3] ?? null };
}

/** Compare two dotted versions numerically ('1.10.0' is after '1.9.2'). */
export function comparePackVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d) return d;
  }
  return 0;
}
